import React, { useEffect, useMemo, useReducer, useRef, useState } from "react";
import openSocket from "../../services/socket-io";
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Paper,
  Select,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Switch,
  TextField,
  Tooltip,
  Typography,
  Divider,
  CircularProgress,
  makeStyles,
} from "@material-ui/core";
import {
  AccountTree,
  Add,
  Close,
  DeleteOutline,
  Edit,
  FileCopy,
  Fullscreen,
  FullscreenExit,
  DragIndicator,
} from "@material-ui/icons";
import SearchIcon from "@material-ui/icons/Search";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import ConfirmationModal from "../../components/ConfirmationModal";
import ChatbotModal from "../../components/ChatbotModal";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";
import {
  buildWhatsappTabs,
  getWhatsappTabParams,
  orphanTabPrefix,
} from "../../utils/connectionsTabs";

const NO_FLOW_AVAILABLE = "__no_flow_available__";
const NO_SEQUENCE_AVAILABLE = "__no_sequence_available__";
const ALL_CATEGORIES = "__all_categories__";
const ALL_FLOWS = "__all_flows__";
const CATEGORY_DRAG_TYPE = "text/chatbot-category";
const SEQUENCE_DRAG_TYPE = "text/chatbot-sequence";

const recordOwnsWhatsapp = (record, connectionId) => {
  if (!connectionId) return true;
  const selectedValue = String(connectionId);

  if (selectedValue.startsWith(orphanTabPrefix)) {
    return String(record?.orphanedConnectionId || "") ===
      selectedValue.replace(orphanTabPrefix, "");
  }

  const ids = [record?.connectionId, ...(record?.sharedConnectionIds || [])]
    .map(Number)
    .filter(id => !Number.isNaN(id));
  return ids.includes(Number(selectedValue));
};

const reducer = (state, action) => {
  if (action.type === "SET_SEQUENCES") {
    return action.payload;
  }

  if (action.type === "LOAD_SEQUENCES") {
    const sequences = action.payload;
    const newSequences = [];

    sequences.forEach(sequence => {
      const sequenceIndex = state.findIndex(item => item.id === sequence.id);
      if (sequenceIndex !== -1) {
        state[sequenceIndex] = sequence;
      } else {
        newSequences.push(sequence);
      }
    });

    return [...state, ...newSequences];
  }

  if (action.type === "UPDATE_SEQUENCE") {
    const sequence = action.payload;
    const sequenceIndex = state.findIndex(item => item.id === sequence.id);

    if (sequenceIndex !== -1) {
      state[sequenceIndex] = sequence;
      return [...state];
    }

    return [sequence, ...state];
  }

  if (action.type === "DELETE_SEQUENCE") {
    const sequenceId = action.payload;
    const sequenceIndex = state.findIndex(item => item.id === sequenceId);
    if (sequenceIndex !== -1) state.splice(sequenceIndex, 1);
    return [...state];
  }

  if (action.type === "REORDER_SEQUENCES") {
    const sequenceMap = new Map(
      action.payload.map(sequence => [Number(sequence.id), sequence])
    );
    return state.map(sequence => sequenceMap.get(Number(sequence.id)) || sequence);
  }

  if (action.type === "RESET") return [];
};

const useStyles = makeStyles(theme => ({
  headerContent: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      gap: theme.spacing(1),
    },
  },
  headerButtons: {
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gridTemplateColumns: "1fr",
      gap: theme.spacing(1),
      marginLeft: 0,
      "& > *": {
        margin: 0,
      },
      "& .MuiTextField-root": {
        width: "100%",
      },
      "& .MuiButton-root": {
        minHeight: 42,
      },
      "& .MuiButton-label": {
        whiteSpace: "normal",
        lineHeight: 1.15,
      },
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "auto",
    margin: 20,
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("xs")]: {
      margin: theme.spacing(1),
      padding: theme.spacing(1),
      border: 0,
      boxShadow: "none",
      backgroundColor: "transparent",
    },
  },
  bulkActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
    [theme.breakpoints.down("xs")]: {
      padding: theme.spacing(1, 0),
      alignItems: "stretch",
      "& .MuiButton-root": {
        flex: "1 1 auto",
      },
    },
  },
  bulkActionsText: {
    fontWeight: 600,
    marginRight: "auto",
  },
  tableWrapper: {
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  categorySection: {
    marginTop: theme.spacing(2),
    position: "relative",
    "&:first-of-type": {
      marginTop: theme.spacing(1),
    },
  },
  draggingRow: {
    opacity: 0.55,
  },
  dragHandleCell: {
    width: 42,
  },
  dragHandle: {
    color: theme.palette.text.secondary,
    cursor: "grab",
    display: "inline-flex",
    verticalAlign: "middle",
    "&:active": {
      cursor: "grabbing",
    },
  },
  dragHandleDisabled: {
    cursor: "not-allowed",
    opacity: 0.36,
  },
  rowDropBefore: {
    boxShadow: `inset 0 3px 0 ${theme.palette.primary.main}`,
  },
  rowDropAfter: {
    boxShadow: `inset 0 -3px 0 ${theme.palette.primary.main}`,
  },
  categoryDropBefore: {
    "&:before": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      content: '""',
      height: 4,
      left: 0,
      position: "absolute",
      right: 0,
      top: -8,
      zIndex: 3,
    },
  },
  categoryDropAfter: {
    "&:after": {
      backgroundColor: theme.palette.primary.main,
      borderRadius: 999,
      bottom: -8,
      boxShadow: `0 0 0 4px ${theme.palette.primary.main}22`,
      content: '""',
      height: 4,
      left: 0,
      position: "absolute",
      right: 0,
      zIndex: 3,
    },
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  categoryTitle: {
    fontWeight: 600,
  },
  categoryCount: {
    color: theme.palette.text.secondary,
  },
  mobileCards: {
    display: "none",
    [theme.breakpoints.down("xs")]: {
      display: "grid",
      gap: theme.spacing(1.5),
    },
  },
  mobileCard: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    padding: theme.spacing(1.5),
  },
  mobileCategorySection: {
    display: "grid",
    gap: theme.spacing(1.5),
  },
  mobileCardHeader: {
    display: "grid",
    gridTemplateColumns: "auto 1fr auto",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  mobileSequenceName: {
    fontWeight: 600,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  mobileSequenceMeta: {
    color: theme.palette.text.secondary,
    fontSize: 13,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  mobileCardGrid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: theme.spacing(1.25),
    marginTop: theme.spacing(1.25),
  },
  mobileField: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    minHeight: 32,
  },
  mobileLabel: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
    whiteSpace: "nowrap",
  },
  mobileValue: {
    minWidth: 0,
    textAlign: "right",
    wordBreak: "break-word",
  },
  mobileActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(0.5),
  },
  paginationBar: {
    display: "flex",
    justifyContent: "flex-end",
    borderTop: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("xs")]: {
      justifyContent: "center",
      "& .MuiTablePagination-toolbar": {
        flexWrap: "wrap",
        justifyContent: "center",
        paddingLeft: 0,
        paddingRight: 0,
      },
      "& .MuiTablePagination-spacer": {
        display: "none",
      },
    },
  },
  viewSwitch: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
  },
  listFilters: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(0.5),
    flexWrap: "wrap",
    "& .MuiFormControl-root": {
      minWidth: 210,
    },
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column",
      "& .MuiFormControl-root": {
        minWidth: 0,
        width: "100%",
      },
    },
  },
  flowToolbar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0.5),
    flexWrap: "wrap",
  },
  flowHint: {
    color: theme.palette.text.secondary,
  },
  flowShell: {
    display: "flex",
    flexDirection: "column",
    minHeight: 0,
  },
  flowShellFullscreen: {
    position: "fixed",
    inset: 0,
    zIndex: theme.zIndex.drawer + 1,
    width: "100vw",
    height: "100vh",
    padding: theme.spacing(1.5),
    boxSizing: "border-box",
    backgroundColor: theme.palette.background.paper,
  },
  flowCanvasWrap: {
    minHeight: 540,
    overflow: "auto",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: "#f7f8fa",
    position: "relative",
    ...theme.scrollbarStyles,
  },
  flowCanvasWrapFullscreen: {
    flex: 1,
    minHeight: 0,
  },
  flowCanvas: {
    position: "relative",
    minWidth: 980,
    minHeight: 540,
  },
  flowEdges: {
    position: "absolute",
    inset: 0,
    pointerEvents: "none",
    zIndex: 1,
  },
  flowEdgeAction: {
    position: "absolute",
    zIndex: 3,
    width: 24,
    height: 24,
    minWidth: 24,
    padding: 0,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.16)",
    "&:hover": {
      backgroundColor: theme.palette.error.light,
      color: theme.palette.common.white,
    },
  },
  flowNode: {
    position: "absolute",
    zIndex: 2,
    width: 260,
    height: 156,
    padding: theme.spacing(1.25),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 8,
    backgroundColor: theme.palette.background.paper,
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    boxSizing: "border-box",
    cursor: "default",
  },
  flowNodeInactive: {
    opacity: 0.62,
  },
  flowNodeConnecting: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}22, 0 4px 12px rgba(0, 0, 0, 0.08)`,
  },
  flowNodeDragging: {
    boxShadow: "0 8px 22px rgba(0, 0, 0, 0.18)",
    zIndex: 4,
  },
  flowNodeHeader: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  flowNodeTitle: {
    fontWeight: 700,
    lineHeight: 1.25,
    wordBreak: "break-word",
  },
  flowNodeMeta: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.35,
    wordBreak: "break-word",
  },
  flowNodeFooter: {
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: theme.spacing(0.75),
  },
  flowHandle: {
    position: "absolute",
    top: "50%",
    width: 14,
    height: 14,
    borderRadius: "50%",
    border: `2px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.primary.main,
    boxShadow: "0 1px 5px rgba(0, 0, 0, 0.28)",
    transform: "translateY(-50%)",
    zIndex: 5,
  },
  flowHandleOut: {
    right: -8,
    cursor: "grab",
  },
  flowHandleIn: {
    left: -8,
    cursor: "copy",
    backgroundColor: "#059669",
  },
  flowHandleActive: {
    boxShadow: `0 0 0 5px ${theme.palette.primary.main}22, 0 1px 5px rgba(0, 0, 0, 0.28)`,
  },
  flowActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  flowZoomActions: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  flowCanvasPannable: {
    cursor: "grab",
    "&:active": {
      cursor: "grabbing",
    },
  },
  edgeLegend: {
    display: "flex",
    gap: theme.spacing(1),
    flexWrap: "wrap",
  },
  edgeLegendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    color: theme.palette.text.secondary,
    fontSize: 12,
  },
  edgeLegendColor: {
    width: 18,
    height: 3,
    borderRadius: 999,
  },
}));

const matchTypeLabels = {
  exact: "Exacta",
  contains: "Contiene",
  conditional: "Condicional",
  anything: "Cualquier cosa",
  newChat: "Nuevo chat",
};

const getMatchTypeLabel = sequence =>
  sequence.matchType === "conditional"
    ? "Condicional"
    : matchTypeLabels[sequence.matchType] || sequence.matchType;

const edgeConfig = {
  parent: { label: "Submenú", color: "#3f51b5" },
  outOfHours: { label: "Fuera de horario", color: "#7c3aed" },
  inactivity: { label: "Inactividad", color: "#d97706" },
  followup: { label: "Seguimiento", color: "#059669" },
  followupCancel: { label: "Cancelar seguimiento", color: "#dc2626" },
};

const getSequenceEdges = sequences => {
  const sequenceIds = new Set(sequences.map(sequence => Number(sequence.id)));
  const edgeIds = new Set();
  const edges = [];
  const addEdge = edge => {
    if (edgeIds.has(edge.id)) return;
    edgeIds.add(edge.id);
    edges.push(edge);
  };

  sequences.forEach(sequence => {
    if (sequence.parentId && sequenceIds.has(Number(sequence.parentId))) {
      addEdge({
        id: `parent-${sequence.parentId}-${sequence.id}`,
        sourceId: Number(sequence.parentId),
        targetId: Number(sequence.id),
        type: "parent",
      });
    }

    if (
      sequence.outOfHoursSequenceId &&
      sequenceIds.has(Number(sequence.outOfHoursSequenceId))
    ) {
      addEdge({
        id: `out-${sequence.id}-${sequence.outOfHoursSequenceId}`,
        sourceId: Number(sequence.id),
        targetId: Number(sequence.outOfHoursSequenceId),
        type: "outOfHours",
      });
    }

    (sequence.blocks || []).forEach((block, index) => {
      [
        ["inactivitySequenceId", "inactivity"],
        ["followupSequenceId", "followup"],
        ["followupCancelSequenceId", "followupCancel"],
      ].forEach(([field, type]) => {
        const targetId = Number(block[field] || 0);
        if (!targetId || !sequenceIds.has(targetId)) return;
        addEdge({
          id: `${type}-${sequence.id}-${targetId}`,
          sourceId: Number(sequence.id),
          targetId,
          type,
        });
      });
    });
  });

  return edges;
};

const getSequenceDepths = sequences => {
  const map = new Map(sequences.map(sequence => [Number(sequence.id), sequence]));
  const depthCache = new Map();

  const resolveDepth = (sequence, stack = new Set()) => {
    const id = Number(sequence.id);
    if (depthCache.has(id)) return depthCache.get(id);
    if (!sequence.parentId || !map.has(Number(sequence.parentId))) {
      depthCache.set(id, 0);
      return 0;
    }
    if (stack.has(id)) {
      depthCache.set(id, 0);
      return 0;
    }

    stack.add(id);
    const parent = map.get(Number(sequence.parentId));
    const depth = resolveDepth(parent, stack) + 1;
    depthCache.set(id, depth);
    return depth;
  };

  sequences.forEach(sequence => resolveDepth(sequence));
  return depthCache;
};

const getFlowLayoutNodes = flow => {
  const layout = flow?.layout || {};
  return layout.nodes || layout;
};

const getFlowSequenceIds = flow => {
  const layout = flow?.layout || {};
  if (Array.isArray(layout.sequenceIds)) return layout.sequenceIds.map(Number);
  return Object.keys(getFlowLayoutNodes(flow)).map(Number).filter(Boolean);
};

const normalizeFlowSequences = sequences =>
  Array.from(
    new Map(
      (sequences || [])
        .filter(sequence => sequence?.id)
        .map(sequence => [Number(sequence.id), sequence])
    ).values()
  );

const compareSequences = (left, right) =>
  Number(left.position || 0) - Number(right.position || 0) ||
  String(left.name || "").localeCompare(String(right.name || "")) ||
  Number(left.id || 0) - Number(right.id || 0);

const getOrderedCategories = sequences => {
  const categoryMap = new Map();

  (sequences || []).forEach(sequence => {
    const category = sequence.category || "General";
    const current = categoryMap.get(category);
    if (
      !current ||
      Number(sequence.categoryPosition || 0) < Number(current.categoryPosition || 0)
    ) {
      categoryMap.set(category, {
        category,
        categoryPosition: Number(sequence.categoryPosition || 0),
      });
    }
  });

  return Array.from(categoryMap.values())
    .sort(
      (left, right) =>
        Number(left.categoryPosition || 0) -
          Number(right.categoryPosition || 0) ||
        String(left.category || "").localeCompare(String(right.category || ""))
    )
    .map(item => item.category);
};

const buildReorderPayload = sequences =>
  getOrderedCategories(sequences).map((category, categoryIndex) => ({
    category,
    position: categoryIndex,
    sequenceIds: sequences
      .filter(sequence => (sequence.category || "General") === category)
      .sort(compareSequences)
      .map(sequence => sequence.id),
  }));

const getSequenceReferencedIds = sequence => {
  const references = [];
  const addReference = value => {
    const id = Number(value || 0);
    if (id) references.push(id);
  };

  addReference(sequence?.outOfHoursSequenceId);
  (sequence?.blocks || []).forEach(block => {
    addReference(block.inactivitySequenceId);
    addReference(block.followupSequenceId);
    addReference(block.followupCancelSequenceId);
  });

  return references.filter(
    (id, index, list) => list.findIndex(item => item === id) === index
  );
};

const buildFlowLayout = (sequences, savedLayout = {}) => {
  const nodeWidth = 260;
  const nodeHeight = 156;
  const columnGap = 110;
  const siblingGap = 38;
  const rootGap = 96;
  const padding = 36;
  const positions = {};
  const savedNodes = savedLayout.nodes || savedLayout;
  const sequenceMap = new Map(sequences.map(sequence => [Number(sequence.id), sequence]));
  const sequenceOrder = new Map(
    sequences.map((sequence, index) => [Number(sequence.id), index])
  );
  const edgeTypeOrder = Object.keys(edgeConfig).reduce((acc, type, index) => {
    acc[type] = index;
    return acc;
  }, {});
  const edges = getSequenceEdges(sequences);
  const incomingByTarget = new Map();
  const outgoingBySource = new Map();
  const primaryParentByTarget = new Map();
  const treeChildrenBySource = new Map();
  const subtreeHeightCache = new Map();

  const compareSequences = (firstId, secondId) => {
    const first = sequenceMap.get(Number(firstId));
    const second = sequenceMap.get(Number(secondId));
    return (
      String(first?.category || "").localeCompare(String(second?.category || "")) ||
      String(first?.name || "").localeCompare(String(second?.name || "")) ||
      (sequenceOrder.get(Number(firstId)) || 0) -
        (sequenceOrder.get(Number(secondId)) || 0)
    );
  };

  edges.forEach(edge => {
    const targetEdges = incomingByTarget.get(edge.targetId) || [];
    targetEdges.push(edge);
    incomingByTarget.set(edge.targetId, targetEdges);

    const sourceEdges = outgoingBySource.get(edge.sourceId) || [];
    sourceEdges.push(edge);
    outgoingBySource.set(edge.sourceId, sourceEdges);
  });

  outgoingBySource.forEach(sourceEdges => {
    sourceEdges.sort((a, b) => {
      const typeDiff =
        (edgeTypeOrder[a.type] ?? 999) - (edgeTypeOrder[b.type] ?? 999);
      if (typeDiff) return typeDiff;
      return compareSequences(a.targetId, b.targetId);
    });
  });

  incomingByTarget.forEach((targetEdges, targetId) => {
    const sortedEdges = [...targetEdges].sort((a, b) => {
      const typeDiff =
        (edgeTypeOrder[a.type] ?? 999) - (edgeTypeOrder[b.type] ?? 999);
      if (typeDiff) return typeDiff;
      return compareSequences(a.sourceId, b.sourceId);
    });
    primaryParentByTarget.set(Number(targetId), sortedEdges[0]);
  });

  primaryParentByTarget.forEach(edge => {
    const children = treeChildrenBySource.get(edge.sourceId) || [];
    children.push(edge.targetId);
    treeChildrenBySource.set(edge.sourceId, children);
  });

  treeChildrenBySource.forEach(children => {
    children.sort(compareSequences);
  });

  const measureSubtree = (sequenceId, stack = new Set()) => {
    const id = Number(sequenceId);
    if (subtreeHeightCache.has(id)) return subtreeHeightCache.get(id);
    if (stack.has(id)) return nodeHeight;

    stack.add(id);
    const children = (treeChildrenBySource.get(id) || []).filter(childId =>
      sequenceMap.has(Number(childId))
    );
    const childrenHeight = children.length
      ? children.reduce(
          (total, childId) => total + measureSubtree(childId, stack),
          0
        ) +
        (children.length - 1) * siblingGap
      : 0;
    stack.delete(id);

    const subtreeHeight = Math.max(nodeHeight, childrenHeight);
    subtreeHeightCache.set(id, subtreeHeight);
    return subtreeHeight;
  };

  const placed = new Set();
  let maxDepth = 0;

  const placeSubtree = (sequenceId, depth, top, stack = new Set()) => {
    const id = Number(sequenceId);
    if (placed.has(id) || stack.has(id) || !sequenceMap.has(id)) return;

    stack.add(id);
    placed.add(id);
    maxDepth = Math.max(maxDepth, depth);

    const subtreeHeight = measureSubtree(id);
    const savedPosition = savedNodes?.[id] || savedNodes?.[String(id)];
    positions[id] = {
      x:
        savedPosition?.x !== undefined
          ? Number(savedPosition.x)
          : padding + depth * (nodeWidth + columnGap),
      y:
        savedPosition?.y !== undefined
          ? Number(savedPosition.y)
          : top + (subtreeHeight - nodeHeight) / 2,
      width: nodeWidth,
      height: nodeHeight,
    };

    const children = (treeChildrenBySource.get(id) || []).filter(childId =>
      sequenceMap.has(Number(childId))
    );
    const childrenHeight = children.length
      ? children.reduce(
          (total, childId) => total + measureSubtree(childId),
          0
        ) +
        (children.length - 1) * siblingGap
      : 0;
    let childTop = top + Math.max(0, (subtreeHeight - childrenHeight) / 2);

    children.forEach(childId => {
      placeSubtree(childId, depth + 1, childTop, new Set(stack));
      childTop += measureSubtree(childId) + siblingGap;
    });
  };

  const roots = sequences
    .filter(sequence => !primaryParentByTarget.has(Number(sequence.id)))
    .sort((a, b) => compareSequences(a.id, b.id));
  const orphanedRoots = sequences
    .filter(sequence => !roots.some(root => Number(root.id) === Number(sequence.id)))
    .filter(sequence => !placed.has(Number(sequence.id)))
    .sort((a, b) => compareSequences(a.id, b.id));
  let nextRootTop = padding;

  [...roots, ...orphanedRoots].forEach(sequence => {
    if (placed.has(Number(sequence.id))) return;
    const subtreeHeight = measureSubtree(sequence.id);
    placeSubtree(sequence.id, 0, nextRootTop);
    nextRootTop += subtreeHeight + rootGap;
  });

  sequences.forEach(sequence => {
    const id = Number(sequence.id);
    if (placed.has(id)) return;
    const savedPosition = savedNodes?.[id] || savedNodes?.[String(id)];
    positions[id] = {
      x:
        savedPosition?.x !== undefined
          ? Number(savedPosition.x)
          : padding,
      y:
        savedPosition?.y !== undefined
          ? Number(savedPosition.y)
          : nextRootTop,
      width: nodeWidth,
      height: nodeHeight,
    };
    nextRootTop += nodeHeight + rootGap;
  });

  const maxSavedX = Math.max(0, ...Object.values(positions).map(position => position.x));
  const maxSavedY = Math.max(0, ...Object.values(positions).map(position => position.y));

  return {
    positions,
    width: Math.max(
      padding * 2 + (maxDepth + 1) * nodeWidth + maxDepth * columnGap,
      maxSavedX + nodeWidth + padding
    ),
    height: Math.max(
      nextRootTop + padding - rootGap,
      maxSavedY + nodeHeight + padding
    ),
  };
};

const Chatbot = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [sequenceCount, setSequenceCount] = useState(0);
  const [searchParam, setSearchParam] = useState("");
  const [sequences, dispatch] = useReducer(reducer, []);
  const [selectedSequence, setSelectedSequence] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [deletingSequence, setDeletingSequence] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [connections, setConnections] = useState([]);
  const [orphanGroups, setOrphanGroups] = useState([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");
  const [selectedSequenceIds, setSelectedSequenceIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [statusToggleSequence, setStatusToggleSequence] = useState(null);
  const [viewMode, setViewMode] = useState("list");
  const [selectedCategoryFilter, setSelectedCategoryFilter] = useState(ALL_CATEGORIES);
  const [selectedFlowFilter, setSelectedFlowFilter] = useState(ALL_FLOWS);
  const [flowSequences, setFlowSequences] = useState([]);
  const [flows, setFlows] = useState([]);
  const [selectedFlowId, setSelectedFlowId] = useState("");
  const [flowLoading, setFlowLoading] = useState(false);
  const [connectingSequenceId, setConnectingSequenceId] = useState("");
  const [draggingFlowNodeId, setDraggingFlowNodeId] = useState("");
  const [flowZoom, setFlowZoom] = useState(1);
  const [flowModalOpen, setFlowModalOpen] = useState(false);
  const [flowModalMode, setFlowModalMode] = useState("create");
  const [flowName, setFlowName] = useState("");
  const [nodeModalOpen, setNodeModalOpen] = useState(false);
  const [nodeParent, setNodeParent] = useState(null);
  const [nodeName, setNodeName] = useState("");
  const [availableSequenceId, setAvailableSequenceId] = useState("");
  const [deletingFlow, setDeletingFlow] = useState(null);
  const [flowSequenceMenuAnchor, setFlowSequenceMenuAnchor] = useState(null);
  const [flowSequenceMenuSequence, setFlowSequenceMenuSequence] = useState(null);
  const [flowSequenceAction, setFlowSequenceAction] = useState(null);
  const [disconnectingFlowEdge, setDisconnectingFlowEdge] = useState(null);
  const [flowPanMode, setFlowPanMode] = useState(false);
  const [isFlowFullscreen, setIsFlowFullscreen] = useState(false);
  const [draggingCategory, setDraggingCategory] = useState("");
  const [draggingSequenceId, setDraggingSequenceId] = useState("");
  const [categoryDropTarget, setCategoryDropTarget] = useState(null);
  const [sequenceDropTarget, setSequenceDropTarget] = useState(null);
  const flowCanvasRef = useRef(null);
  const flowDragRef = useRef(null);
  const flowPanRef = useRef(null);

  const whatsappTabs = useMemo(
    () => buildWhatsappTabs(connections, orphanGroups),
    [connections, orphanGroups]
  );
  const selectedWhatsappTab = whatsappTabs.find(
    tab => String(tab.value) === String(selectedConnectionId)
  );
  const sequenceMatchesSearch = sequence => {
    const search = String(searchParam || "").trim().toLowerCase();
    if (!search) return true;

    return [
      sequence?.name,
      sequence?.category,
      sequence?.trigger,
      sequence?.matchType,
    ].some(value => String(value || "").toLowerCase().includes(search));
  };
  const selectedFilterFlow = useMemo(
    () => flows.find(flow => String(flow.id) === String(selectedFlowFilter)) || null,
    [flows, selectedFlowFilter]
  );
  const selectedFilterFlowSequenceIds = useMemo(
    () => new Set(getFlowSequenceIds(selectedFilterFlow).map(Number)),
    [selectedFilterFlow]
  );
  const listSourceSequences = useMemo(
    () =>
      selectedFilterFlow
        ? flowSequences.filter(sequence =>
            selectedFilterFlowSequenceIds.has(Number(sequence.id))
          )
        : sequences,
    [flowSequences, selectedFilterFlow, selectedFilterFlowSequenceIds, sequences]
  );
  const categorySourceSequences = useMemo(
    () =>
      selectedFilterFlow
        ? flowSequences.filter(sequence =>
            selectedFilterFlowSequenceIds.has(Number(sequence.id))
          )
        : flowSequences,
    [flowSequences, selectedFilterFlow, selectedFilterFlowSequenceIds]
  );
  const categories = useMemo(
    () => getOrderedCategories(categorySourceSequences),
    [categorySourceSequences]
  );
  const filteredSequences = useMemo(
    () =>
      listSourceSequences.filter(sequence => {
        if (!sequenceMatchesSearch(sequence)) return false;
        if (
          selectedCategoryFilter !== ALL_CATEGORIES &&
          (sequence.category || "General") !== selectedCategoryFilter
        ) {
          return false;
        }
        return true;
      }),
    [listSourceSequences, searchParam, selectedCategoryFilter]
  );
  const groupedSequences = useMemo(
    () =>
      categories.map(category => ({
        category,
        sequences: filteredSequences
          .filter(sequence => (sequence.category || "General") === category)
          .sort(compareSequences),
      })).filter(group => group.sequences.length),
    [categories, filteredSequences]
  );
  const selectedFlow = useMemo(
    () => flows.find(flow => String(flow.id) === String(selectedFlowId)) || null,
    [flows, selectedFlowId]
  );
  const selectedFlowSequenceIds = useMemo(
    () => new Set(getFlowSequenceIds(selectedFlow).map(Number)),
    [selectedFlow]
  );
  const selectedFlowSequences = useMemo(
    () =>
      selectedFlow
        ? normalizeFlowSequences(
            flowSequences.filter(sequence =>
              selectedFlowSequenceIds.has(Number(sequence.id))
            )
          )
        : [],
    [flowSequences, selectedFlow, selectedFlowSequenceIds]
  );
  const availableFlowSequences = useMemo(
    () =>
      selectedFlow
        ? normalizeFlowSequences(
            flowSequences.filter(sequence =>
              !selectedFlowSequenceIds.has(Number(sequence.id))
            )
          )
        : [],
    [flowSequences, selectedFlow, selectedFlowSequenceIds]
  );
  const flowEdges = useMemo(
    () => getSequenceEdges(selectedFlowSequences),
    [selectedFlowSequences]
  );
  const flowLayout = useMemo(
    () => buildFlowLayout(selectedFlowSequences, getFlowLayoutNodes(selectedFlow)),
    [selectedFlowSequences, selectedFlow]
  );

  useEffect(() => {
    const fetchConnections = async () => {
      try {
        const [{ data }, { data: orphanData }] = await Promise.all([
          api.get("/connections/"),
          api.get("/connections/orphan-groups")
        ]);
        const loadedConnections = data || [];
        const loadedOrphans = orphanData || [];
        const tabs = buildWhatsappTabs(loadedConnections, loadedOrphans);
        setConnections(loadedConnections);
        setOrphanGroups(loadedOrphans);
        setSelectedConnectionId(prevState => prevState || tabs[0]?.value || "");
      } catch (err) {
        toastError(err);
      }
    };

    fetchConnections();
  }, []);

  useEffect(() => {
    setPageNumber(1);
    setSelectedSequenceIds([]);
  }, [searchParam, selectedConnectionId]);

  useEffect(() => {
    setSelectedCategoryFilter(ALL_CATEGORIES);
    setSelectedFlowFilter(ALL_FLOWS);
  }, [selectedConnectionId]);

  useEffect(() => {
    if (
      selectedCategoryFilter !== ALL_CATEGORIES &&
      !categories.includes(selectedCategoryFilter)
    ) {
      setSelectedCategoryFilter(ALL_CATEGORIES);
    }
  }, [categories, selectedCategoryFilter]);

  useEffect(() => {
    if (
      selectedFlowFilter !== ALL_FLOWS &&
      !flows.some(flow => String(flow.id) === String(selectedFlowFilter))
    ) {
      setSelectedFlowFilter(ALL_FLOWS);
    }
  }, [flows, selectedFlowFilter]);

  useEffect(() => {
    setSelectedSequenceIds([]);
    setPageNumber(1);
  }, [selectedCategoryFilter, selectedFlowFilter]);

  useEffect(() => {
    const sequenceIds = new Set(filteredSequences.map(sequence => String(sequence.id)));
    setSelectedSequenceIds(prevState =>
      prevState.filter(sequenceId => sequenceIds.has(String(sequenceId)))
    );
  }, [filteredSequences]);

  useEffect(() => {
    setLoading(true);
    const fetchSequences = async () => {
      try {
        const { data } = await api.get("/chatbot/sequences", {
          params: {
            searchParam,
            pageNumber,
            rowsPerPage,
            category:
              selectedCategoryFilter !== ALL_CATEGORIES &&
              selectedFlowFilter === ALL_FLOWS
                ? selectedCategoryFilter
                : undefined,
            ...getWhatsappTabParams(selectedConnectionId)
          },
        });
        dispatch({ type: "SET_SEQUENCES", payload: data.sequences });
        setSequenceCount(data.count || 0);
        setLoading(false);
      } catch (err) {
        toastError(err);
        setLoading(false);
      }
    };
    fetchSequences();
  }, [
    searchParam,
    pageNumber,
    rowsPerPage,
    refreshKey,
    selectedConnectionId,
    selectedCategoryFilter,
    selectedFlowFilter,
  ]);

  useEffect(() => {
    const fetchFlows = async () => {
      try {
        const { data } = await api.get("/chatbot/flows", {
          params: getWhatsappTabParams(selectedConnectionId),
        });
        const loadedFlows = data.flows || [];
        setFlows(loadedFlows);
        setSelectedFlowId(prevState =>
          loadedFlows.some(flow => String(flow.id) === String(prevState))
            ? prevState
            : loadedFlows[0]?.id || ""
        );
      } catch (err) {
        toastError(err);
      }
    };

    fetchFlows();
  }, [selectedConnectionId, refreshKey]);

  useEffect(() => {
    if (viewMode !== "flow") setIsFlowFullscreen(false);
  }, [viewMode]);

  useEffect(() => {
    const fetchFlowSequences = async () => {
      setFlowLoading(true);
      try {
        const { data } = await api.get("/chatbot/sequences-flow", {
          params: getWhatsappTabParams(selectedConnectionId),
        });
        setFlowSequences(normalizeFlowSequences(data.sequences));
      } catch (err) {
        toastError(err);
      } finally {
        setFlowLoading(false);
      }
    };

    fetchFlowSequences();
  }, [selectedConnectionId, refreshKey]);

  useEffect(() => {
    const handleKeyDown = event => {
      if (event.key === "Escape") setIsFlowFullscreen(false);
    };

    if (isFlowFullscreen) {
      window.addEventListener("keydown", handleKeyDown);
    }

    const resizeTimer = setTimeout(() => {
      window.dispatchEvent(new Event("resize"));
    }, 80);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      clearTimeout(resizeTimer);
    };
  }, [isFlowFullscreen]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("chatbotSequence", data => {
      if (data.action === "update" || data.action === "create") {
        if (
          (!selectedConnectionId ||
            recordOwnsWhatsapp(data.sequence, selectedConnectionId)) &&
          sequenceMatchesSearch(data.sequence)
        ) {
          dispatch({ type: "UPDATE_SEQUENCE", payload: data.sequence });
        } else if (data.action === "update") {
          dispatch({ type: "DELETE_SEQUENCE", payload: +data.sequence.id });
        }
        setFlowSequences(prevState => {
          if (
            selectedConnectionId &&
            !recordOwnsWhatsapp(data.sequence, selectedConnectionId)
          ) {
            return normalizeFlowSequences(
              prevState.filter(sequence => Number(sequence.id) !== Number(data.sequence.id))
            );
          }

          const index = prevState.findIndex(
            sequence => Number(sequence.id) === Number(data.sequence.id)
          );
          if (index === -1) {
            return normalizeFlowSequences([...prevState, data.sequence]);
          }
          const nextState = [...prevState];
          nextState[index] = data.sequence;
          return normalizeFlowSequences(nextState);
        });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_SEQUENCE", payload: +data.sequenceId });
        setFlowSequences(prevState =>
          normalizeFlowSequences(
            prevState.filter(sequence => Number(sequence.id) !== Number(data.sequenceId))
          )
        );
      }

      if (data.action === "reorder") {
        const nextSequences = normalizeFlowSequences(
          (data.sequences || []).filter(sequence =>
            recordOwnsWhatsapp(sequence, selectedConnectionId)
          )
        );
        dispatch({ type: "REORDER_SEQUENCES", payload: nextSequences });
        setFlowSequences(nextSequences);
      }
    });

    return () => socket.disconnect();
  }, [selectedConnectionId, searchParam]);

  useEffect(() => {
    if (!draggingFlowNodeId) return undefined;

    const handleMouseMove = event => {
      const dragState = flowDragRef.current;
      const canvasRect = flowCanvasRef.current?.getBoundingClientRect();
      if (!dragState || !canvasRect) return;

      const x = Math.max(
        24,
        (event.clientX -
          canvasRect.left +
          flowCanvasRef.current.scrollLeft) /
          dragState.zoom -
          dragState.offsetX
      );
      const y = Math.max(
        24,
        (event.clientY -
          canvasRect.top +
          flowCanvasRef.current.scrollTop) /
          dragState.zoom -
          dragState.offsetY
      );
      const nextLayout = {
        ...dragState.layout,
        sequenceIds: dragState.layout.sequenceIds || [],
        nodes: {
          ...(dragState.layout.nodes || {}),
          [dragState.sequenceId]: { x, y },
        },
      };

      flowDragRef.current = {
        ...dragState,
        layout: nextLayout,
        moved: true,
      };

      setFlows(prevState =>
        prevState.map(flow =>
          Number(flow.id) === Number(selectedFlowId)
            ? { ...flow, layout: nextLayout }
            : flow
        )
      );
    };

    const handleMouseUp = async () => {
      const dragState = flowDragRef.current;
      flowDragRef.current = null;
      setDraggingFlowNodeId("");
      if (dragState?.layout && dragState.moved) {
        await saveFlowLayout(dragState.layout);
      }
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [draggingFlowNodeId, selectedFlowId]);

  useEffect(() => {
    const handleMouseMove = event => {
      const pan = flowPanRef.current;
      if (!pan || !flowCanvasRef.current) return;
      flowCanvasRef.current.scrollLeft = pan.scrollLeft - (event.clientX - pan.startX);
      flowCanvasRef.current.scrollTop = pan.scrollTop - (event.clientY - pan.startY);
    };

    const handleMouseUp = () => {
      flowPanRef.current = null;
    };

    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, []);

  const handleSearch = event => {
    setSearchParam(event.target.value);
  };

  const handleChangePage = (event, newPage) => {
    setPageNumber(newPage + 1);
    setSelectedSequenceIds([]);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(Number(event.target.value));
    setPageNumber(1);
    setSelectedSequenceIds([]);
  };

  const handleOpenModal = () => {
    setSelectedSequence(null);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedSequence(null);
    setModalOpen(false);
  };

  const addReferencedSequencesToSelectedFlow = async savedSequence => {
    if (!selectedFlow || !savedSequence?.id) return;
    if (!selectedFlowSequenceIds.has(Number(savedSequence.id))) return;

    const referencedIds = getSequenceReferencedIds(savedSequence);
    if (!referencedIds.length) return;

    const currentSequenceIds = getFlowSequenceIds(selectedFlow);
    const currentSequenceIdSet = new Set(currentSequenceIds.map(Number));
    const availableSequenceIds = new Set(
      flowSequences.map(sequence => Number(sequence.id))
    );
    const missingReferencedIds = referencedIds.filter(
      referenceId =>
        !currentSequenceIdSet.has(Number(referenceId)) &&
        availableSequenceIds.has(Number(referenceId))
    );

    if (!missingReferencedIds.length) return;

    await saveFlowLayout(
      buildAutoArrangedFlowPatch(
        [...currentSequenceIds, ...missingReferencedIds],
        flowSequences.map(sequence =>
          Number(sequence.id) === Number(savedSequence.id) ? savedSequence : sequence
        )
      )
    );
  };

  const handleSavedModal = async savedSequence => {
    if (savedSequence?.id) {
      dispatch({ type: "UPDATE_SEQUENCE", payload: savedSequence });
      setFlowSequences(prevState => {
        const index = prevState.findIndex(
          sequence => Number(sequence.id) === Number(savedSequence.id)
        );
        if (index === -1) return normalizeFlowSequences([...prevState, savedSequence]);
        const nextState = [...prevState];
        nextState[index] = savedSequence;
        return normalizeFlowSequences(nextState);
      });
      await addReferencedSequencesToSelectedFlow(savedSequence);
    }
  };

  const handleEdit = sequence => {
    setSelectedSequence(sequence);
    setModalOpen(true);
  };

  const handleDelete = async sequenceId => {
    try {
      await api.delete(`/chatbot/sequences/${sequenceId}`);
      dispatch({ type: "DELETE_SEQUENCE", payload: Number(sequenceId) });
      setFlowSequences(prevState =>
        normalizeFlowSequences(
          prevState.filter(sequence => Number(sequence.id) !== Number(sequenceId))
        )
      );
      toast.success(i18n.t("chatbot.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingSequence(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const buildDuplicateSequencePayload = sequence => ({
    name: `${sequence.name} - copia`,
    category: sequence.category || "General",
    trigger: sequence.trigger || "",
    matchType: sequence.matchType || "exact",
    requireAllTriggers: Boolean(sequence.requireAllTriggers),
    connectionId: sequence.connectionId || "",
    sharedConnectionIds: sequence.sharedConnectionIds || [],
    parentId: sequence.parentId || "",
    isActive: sequence.isActive !== false,
    scheduleEnabled: Boolean(sequence.scheduleEnabled),
    quickReplyEnabled: Boolean(sequence.quickReplyEnabled),
    quickReplyMatchType: sequence.quickReplyMatchType || "exact",
    quickReplyTrigger: sequence.quickReplyTrigger || "",
    schedule: sequence.schedule || null,
    executionMode: sequence.executionMode || "unlimited",
    executionLimit: sequence.executionLimit || "",
    executionInterval: sequence.executionInterval || "",
    executionIntervalUnit: sequence.executionIntervalUnit || "",
    transferType: sequence.transferType || "",
    transferQueueId: sequence.transferQueueId || "",
    transferUserId: sequence.transferUserId || "",
    addLabelIds: sequence.addLabelIds || [],
    removeLabelIds: sequence.removeLabelIds || [],
    aiAction: sequence.aiAction || "",
    funnelActionFunnelId: sequence.funnelActionFunnelId || "",
    funnelActionStageId: sequence.funnelActionStageId || "",
    aiInitialMessage: sequence.aiInitialMessage || "",
    aiPrompt: sequence.aiPrompt || "",
    orchestratorModeEnabled: Boolean(sequence.orchestratorModeEnabled),
    useSequencesTool: sequence.useSequencesTool !== false,
    useWasheetsTool: sequence.useWasheetsTool !== false,
    useWebSearchTool: sequence.useWebSearchTool !== false,
    useAgendaTool: sequence.useAgendaTool !== false,
    useOrdersTool: sequence.useOrdersTool !== false,
    useWebhookTools: sequence.useWebhookTools !== false,
    useContactTool: sequence.useContactTool !== false,
    useProcessesTool: sequence.useProcessesTool === true,
    naturalMessageModeEnabled: sequence.naturalMessageModeEnabled !== false,
    outOfHoursSequenceId: sequence.outOfHoursSequenceId || "",
    noMatchMessage: sequence.noMatchMessage || "",
    noMatchAiEnabled: Boolean(sequence.noMatchAiEnabled),
    noMatchAiPrompt: sequence.noMatchAiPrompt || "",
    afterExecutionBehavior: sequence.afterExecutionBehavior || "root",
    afterExecutionSequenceId: sequence.afterExecutionSequenceId || "",
    blocks: sequence.blocks || [],
  });

  const handleDuplicate = async sequence => {
    try {
      const { data: sequenceToDuplicate } = await api.get(
        `/chatbot/sequences/${sequence.id}`
      );
      const { data } = await api.post(
        "/chatbot/sequences",
        buildDuplicateSequencePayload(sequenceToDuplicate)
      );
      dispatch({ type: "UPDATE_SEQUENCE", payload: data });
      setFlowSequences(prevState =>
        normalizeFlowSequences([...prevState, data])
      );
      toast.success("Secuencia duplicada.");
    } catch (err) {
      toastError(err);
    }
  };

  const canReorderSequences =
    viewMode === "list" &&
    selectedFlowFilter === ALL_FLOWS &&
    !String(searchParam || "").trim();
  const canReorderCategories =
    canReorderSequences && selectedCategoryFilter === ALL_CATEGORIES;

  const syncReorderedSequences = async nextSequences => {
    const normalizedSequences = normalizeFlowSequences(nextSequences);
    setFlowSequences(normalizedSequences);
    dispatch({ type: "REORDER_SEQUENCES", payload: normalizedSequences });

    try {
      const { data } = await api.put("/chatbot/sequences/reorder", {
        categories: buildReorderPayload(normalizedSequences),
      });
      const savedSequences = normalizeFlowSequences(
        (data.sequences || []).filter(sequence =>
          recordOwnsWhatsapp(sequence, selectedConnectionId)
        )
      );
      setFlowSequences(savedSequences);
      dispatch({ type: "REORDER_SEQUENCES", payload: savedSequences });
    } catch (err) {
      toastError(err);
      setRefreshKey(prevState => prevState + 1);
    }
  };

  const moveCategory = (sourceCategory, targetCategory, placement = "after") => {
    if (!canReorderCategories || !sourceCategory || !targetCategory) return;
    if (sourceCategory === targetCategory) return;

    const withoutSource = categories.filter(category => category !== sourceCategory);
    const targetIndex = withoutSource.findIndex(category => category === targetCategory);
    const insertIndex =
      targetIndex >= 0
        ? targetIndex + (placement === "after" ? 1 : 0)
        : withoutSource.length;
    withoutSource.splice(insertIndex, 0, sourceCategory);

    const categoryPositionByName = new Map(
      withoutSource.map((category, index) => [category, index])
    );
    syncReorderedSequences(
      flowSequences.map(sequence => ({
        ...sequence,
        categoryPosition:
          categoryPositionByName.get(sequence.category || "General") ??
          sequence.categoryPosition,
      }))
    );
    setDraggingCategory("");
    setCategoryDropTarget(null);
  };

  const moveSequence = (sourceId, targetId, category, placement = "after") => {
    if (!canReorderSequences || !sourceId || !targetId || !category) return;
    if (String(sourceId) === String(targetId)) return;

    const categorySequences = flowSequences
      .filter(sequence => (sequence.category || "General") === category)
      .sort(compareSequences);
    const draggedSequence = categorySequences.find(
      sequence => String(sequence.id) === String(sourceId)
    );
    if (!draggedSequence) return;

    const withoutSource = categorySequences.filter(
      sequence => String(sequence.id) !== String(sourceId)
    );
    const targetIndex = withoutSource.findIndex(
      sequence => String(sequence.id) === String(targetId)
    );
    const insertIndex =
      targetIndex >= 0
        ? targetIndex + (placement === "after" ? 1 : 0)
        : withoutSource.length;
    withoutSource.splice(insertIndex, 0, draggedSequence);

    const positionById = new Map(
      withoutSource.map((sequence, index) => [String(sequence.id), index])
    );
    syncReorderedSequences(
      flowSequences.map(sequence =>
        (sequence.category || "General") === category
          ? { ...sequence, position: positionById.get(String(sequence.id)) ?? sequence.position }
          : sequence
      )
    );
    setDraggingSequenceId("");
    setSequenceDropTarget(null);
  };

  const allSequencesSelected =
    filteredSequences.length > 0 &&
    filteredSequences.every(sequence =>
      selectedSequenceIds.includes(String(sequence.id))
    );
  const selectedSequenceCount = selectedSequenceIds.length;

  const toggleSequenceSelection = sequenceId => {
    const normalizedId = String(sequenceId);
    setSelectedSequenceIds(prevState =>
      prevState.includes(normalizedId)
        ? prevState.filter(id => id !== normalizedId)
        : [...prevState, normalizedId]
    );
  };

  const toggleAllSequencesSelection = () => {
    if (allSequencesSelected) {
      setSelectedSequenceIds([]);
      return;
    }

    setSelectedSequenceIds(filteredSequences.map(sequence => String(sequence.id)));
  };

  const handleDeleteSelectedSequences = async () => {
    try {
      await Promise.all(
        selectedSequenceIds.map(sequenceId =>
          api.delete(`/chatbot/sequences/${sequenceId}`)
        )
      );
      selectedSequenceIds.forEach(sequenceId =>
        dispatch({ type: "DELETE_SEQUENCE", payload: Number(sequenceId) })
      );
      setSelectedSequenceIds([]);
      toast.success(i18n.t("chatbot.toasts.deleted"));
    } catch (err) {
      toastError(err);
    } finally {
      setBulkDeleteOpen(false);
      setSearchParam("");
      setPageNumber(1);
    }
  };

  const handleConfirmStatusToggle = async () => {
    if (!statusToggleSequence) return;

    const { sequence, nextActive } = statusToggleSequence;

    try {
      const { data } = await api.put(
        `/chatbot/sequences/${sequence.id}/status`,
        { isActive: nextActive }
      );
      dispatch({ type: "UPDATE_SEQUENCE", payload: data });
      toast.success(
        nextActive ? "Secuencia activada." : "Secuencia desactivada."
      );
    } catch (err) {
      toastError(err);
    } finally {
      setStatusToggleSequence(null);
    }
  };

  const handleFlowConnect = async targetSequence => {
    if (!connectingSequenceId) return;
    if (String(connectingSequenceId) === String(targetSequence.id)) {
      setConnectingSequenceId("");
      return;
    }

    try {
      const { data } = await api.put("/chatbot/sequences-flow/connect", {
        sourceId: Number(connectingSequenceId),
        targetId: Number(targetSequence.id),
        type: "parent",
      });
      setFlowSequences(prevState =>
        normalizeFlowSequences(
          prevState.map(sequence =>
            Number(sequence.id) === Number(data.id) ? data : sequence
          )
        )
      );
      dispatch({ type: "UPDATE_SEQUENCE", payload: data });
      toast.success("Secuencia conectada como submenú.");
    } catch (err) {
      toastError(err);
    } finally {
      setConnectingSequenceId("");
    }
  };

  const handleFlowDisconnect = async () => {
    if (!disconnectingFlowEdge) return;

    try {
      const { data } = await api.put("/chatbot/sequences-flow/connect", {
        sourceId: Number(disconnectingFlowEdge.sourceId),
        targetId: Number(disconnectingFlowEdge.targetId),
        type: "parent",
        action: "disconnect",
      });
      setFlowSequences(prevState =>
        normalizeFlowSequences(
          prevState.map(sequence =>
            Number(sequence.id) === Number(data.id) ? data : sequence
          )
        )
      );
      dispatch({ type: "UPDATE_SEQUENCE", payload: data });
      toast.success("Secuencia desconectada.");
    } catch (err) {
      toastError(err);
    } finally {
      setDisconnectingFlowEdge(null);
    }
  };

  const updateSelectedFlow = nextFlow => {
    setFlows(prevState => {
      const index = prevState.findIndex(flow => Number(flow.id) === Number(nextFlow.id));
      if (index === -1) return [...prevState, nextFlow];
      const nextState = [...prevState];
      nextState[index] = nextFlow;
      return nextState;
    });
    setSelectedFlowId(nextFlow.id);
  };

  const openCreateFlowModal = () => {
    setFlowModalMode("create");
    setFlowName("");
    setFlowModalOpen(true);
  };

  const openRenameFlowModal = () => {
    if (!selectedFlow) return;
    setFlowModalMode("rename");
    setFlowName(selectedFlow.name || "");
    setFlowModalOpen(true);
  };

  const handleSaveFlowModal = async () => {
    if (!flowName.trim()) return;

    try {
      const { data } =
        flowModalMode === "rename" && selectedFlow
          ? await api.put(`/chatbot/flows/${selectedFlow.id}`, {
              name: flowName.trim(),
            })
          : await api.post("/chatbot/flows", {
              name: flowName.trim(),
              connectionId:
                selectedConnectionId &&
                !String(selectedConnectionId).startsWith(orphanTabPrefix)
                  ? Number(selectedConnectionId)
                  : null,
              layout: { sequenceIds: [], nodes: {} },
            });
      updateSelectedFlow(data);
      setFlowModalOpen(false);
      toast.success(flowModalMode === "rename" ? "Flujo actualizado." : "Flujo creado.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleDeleteFlow = async () => {
    const flowToDelete = deletingFlow || selectedFlow;
    if (!flowToDelete) return;

    try {
      await api.delete(`/chatbot/flows/${flowToDelete.id}`);
      setFlows(prevState =>
        prevState.filter(flow => Number(flow.id) !== Number(flowToDelete.id))
      );
      setSelectedFlowId("");
      setDeletingFlow(null);
      toast.success("Flujo eliminado.");
    } catch (err) {
      toastError(err);
    }
  };

  const saveFlowLayout = async (patch, flowOverride = selectedFlow) => {
    if (!flowOverride) return null;
    const nextLayout = {
      sequenceIds: getFlowSequenceIds(flowOverride),
      nodes: getFlowLayoutNodes(flowOverride),
      ...(flowOverride.layout || {}),
      ...patch,
    };

    try {
      const { data } = await api.put(`/chatbot/flows/${flowOverride.id}`, {
        layout: nextLayout,
      });
      updateSelectedFlow(data);
      return data;
    } catch (err) {
      toastError(err);
      return null;
    }
  };

  const buildAutoArrangedFlowPatch = (sequenceIds, sequencesPool) => {
    const sequenceIdSet = new Set(sequenceIds.map(Number));
    const nextFlowSequences = normalizeFlowSequences(
      sequencesPool.filter(sequence => sequenceIdSet.has(Number(sequence.id)))
    );
    const arranged = buildFlowLayout(nextFlowSequences, {}).positions;
    const nodes = Object.entries(arranged).reduce((acc, [sequenceId, position]) => {
      acc[sequenceId] = { x: position.x, y: position.y };
      return acc;
    }, {});

    return { sequenceIds, nodes };
  };

  const handleAutoArrangeFlow = async () => {
    if (!selectedFlow) return;
    await saveFlowLayout(
      buildAutoArrangedFlowPatch(getFlowSequenceIds(selectedFlow), flowSequences)
    );
    toast.success("Flujo organizado.");
  };

  const openNodeModal = parentSequence => {
    setNodeParent(parentSequence || null);
    setNodeName("");
    setNodeModalOpen(true);
  };

  const handleAddNode = async () => {
    if (!selectedFlow) {
      toast.error("Primero crea o selecciona un flujo.");
      return;
    }
    if (!nodeName.trim()) return;

    const parentPosition = nodeParent
      ? flowLayout.positions[nodeParent.id] || { x: 36, y: 36 }
      : { x: 36, y: 36 };

    try {
      const { data } = await api.post(`/chatbot/flows/${selectedFlow.id}/submenus`, {
        parentId: nodeParent?.id || null,
        name: nodeName.trim(),
        x: nodeParent ? parentPosition.x + 350 : parentPosition.x,
        y: nodeParent ? parentPosition.y + 174 : parentPosition.y,
      });
      setFlowSequences(prevState =>
        normalizeFlowSequences([...prevState, data.sequence])
      );
      dispatch({ type: "UPDATE_SEQUENCE", payload: data.sequence });
      const nextFlow = data.flow || selectedFlow;
      await saveFlowLayout(
        buildAutoArrangedFlowPatch(
          getFlowSequenceIds(nextFlow),
          [...flowSequences, data.sequence]
        ),
        nextFlow
      );
      setNodeModalOpen(false);
      toast.success(nodeParent ? "Submenú creado." : "Secuencia creada.");
    } catch (err) {
      toastError(err);
    }
  };

  const handleAddExistingSequence = async () => {
    if (!selectedFlow || !availableSequenceId) return;
    const sequence = flowSequences.find(
      item => String(item.id) === String(availableSequenceId)
    );
    if (!sequence) return;

    const nextSequenceIds = [
      ...getFlowSequenceIds(selectedFlow),
      Number(sequence.id),
    ].filter((id, index, list) => list.indexOf(id) === index);
    await saveFlowLayout(
      buildAutoArrangedFlowPatch(nextSequenceIds, flowSequences)
    );
    setAvailableSequenceId("");
    toast.success("Secuencia agregada al flujo.");
  };

  const openFlowSequenceMenu = (event, sequence) => {
    event.stopPropagation();
    setFlowSequenceMenuAnchor(event.currentTarget);
    setFlowSequenceMenuSequence(sequence);
  };

  const closeFlowSequenceMenu = () => {
    setFlowSequenceMenuAnchor(null);
    setFlowSequenceMenuSequence(null);
  };

  const requestFlowSequenceAction = action => {
    if (!flowSequenceMenuSequence) return;
    setFlowSequenceAction({ action, sequence: flowSequenceMenuSequence });
    closeFlowSequenceMenu();
  };

  const handleConfirmFlowSequenceAction = async () => {
    if (!flowSequenceAction) return;

    const { action, sequence } = flowSequenceAction;

    if (action === "remove") {
      if (!selectedFlow) {
        setFlowSequenceAction(null);
        return;
      }
      const sequenceIds = getFlowSequenceIds(selectedFlow).filter(
        sequenceId => Number(sequenceId) !== Number(sequence.id)
      );
      const nodes = { ...getFlowLayoutNodes(selectedFlow) };
      delete nodes[sequence.id];
      delete nodes[String(sequence.id)];

      await saveFlowLayout({ sequenceIds, nodes });
      setFlowSequenceAction(null);
      toast.success("Secuencia quitada del flujo.");
      return;
    }

    await handleDelete(sequence.id);
    setFlowSequenceAction(null);
  };

  const handleFlowPanMouseDown = event => {
    if (!flowPanMode || !flowCanvasRef.current) return;
    if (
      event.target?.closest?.(
        "[data-flow-node='true'], button, input, [data-flow-handle='true']"
      )
    ) {
      return;
    }
    flowPanRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      scrollLeft: flowCanvasRef.current.scrollLeft,
      scrollTop: flowCanvasRef.current.scrollTop,
    };
    event.preventDefault();
  };

  const startFlowNodeDrag = (event, sequence) => {
    if (!selectedFlow) return;
    if (flowPanMode) return;
    if (event.target?.closest?.("button, input, [data-flow-handle='true']")) return;

    const canvasRect = flowCanvasRef.current?.getBoundingClientRect();
    const currentPosition = flowLayout.positions[sequence.id];
    if (!canvasRect || !currentPosition) return;

    setDraggingFlowNodeId(String(sequence.id));
    flowDragRef.current = {
      sequenceId: String(sequence.id),
      offsetX:
        (event.clientX - canvasRect.left + flowCanvasRef.current.scrollLeft) /
          flowZoom -
        currentPosition.x,
      offsetY:
        (event.clientY - canvasRect.top + flowCanvasRef.current.scrollTop) /
          flowZoom -
        currentPosition.y,
      zoom: flowZoom,
      moved: false,
      layout: {
        ...(selectedFlow.layout || {}),
        sequenceIds: getFlowSequenceIds(selectedFlow),
        nodes: getFlowLayoutNodes(selectedFlow),
      },
    };
    event.preventDefault();
  };

  const renderSequenceActions = sequence => (
    <>
      <IconButton size="small" onClick={() => handleEdit(sequence)}>
        <Edit />
      </IconButton>
      <Tooltip title="Duplicar secuencia">
        <IconButton size="small" onClick={() => handleDuplicate(sequence)}>
          <FileCopy />
        </IconButton>
      </Tooltip>
      <IconButton
        size="small"
        onClick={() => {
          setConfirmModalOpen(true);
          setDeletingSequence(sequence);
        }}
      >
        <DeleteOutline />
      </IconButton>
    </>
  );

  const renderStatusToggle = sequence => (
    <Switch
      checked={sequence.isActive}
      color="primary"
      onChange={event =>
        setStatusToggleSequence({
          sequence,
          nextActive: event.target.checked,
        })
      }
      inputProps={{
        "aria-label": sequence.isActive
          ? "Desactivar secuencia"
          : "Activar secuencia",
      }}
    />
  );

  const renderSequenceRow = sequence => {
    const category = sequence.category || "General";
    const isDragging = String(draggingSequenceId) === String(sequence.id);
    const isDropTarget =
      sequenceDropTarget?.sequenceId === String(sequence.id) &&
      sequenceDropTarget?.category === category;

    return (
    <TableRow
      key={sequence.id}
      className={`${isDragging ? classes.draggingRow : ""} ${
        isDropTarget && sequenceDropTarget?.placement === "before"
          ? classes.rowDropBefore
          : ""
      } ${
        isDropTarget && sequenceDropTarget?.placement === "after"
          ? classes.rowDropAfter
          : ""
      }`}
      onDragOver={event => {
        if (!canReorderSequences || !draggingSequenceId) return;
        if (String(draggingSequenceId) === String(sequence.id)) return;
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        const placement =
          event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
        const nextTarget = {
          category,
          sequenceId: String(sequence.id),
          placement,
        };
        setSequenceDropTarget(prevState =>
          prevState?.category === nextTarget.category &&
          prevState?.sequenceId === nextTarget.sequenceId &&
          prevState?.placement === nextTarget.placement
            ? prevState
            : nextTarget
        );
      }}
      onDrop={event => {
        if (!canReorderSequences || !draggingSequenceId) return;
        event.preventDefault();
        event.stopPropagation();
        moveSequence(
          draggingSequenceId,
          sequence.id,
          category,
          sequenceDropTarget?.placement || "after"
        );
      }}
    >
      <TableCell className={classes.dragHandleCell} padding="checkbox">
        <Tooltip
          title={
            canReorderSequences
              ? "Arrastrar secuencia"
              : "Quita la búsqueda o el filtro de flujo para ordenar"
          }
        >
          <span
            className={`${classes.dragHandle} ${
              canReorderSequences ? "" : classes.dragHandleDisabled
            }`}
            draggable={canReorderSequences}
            onDragStart={event => {
              if (!canReorderSequences) return;
              event.dataTransfer.setData(SEQUENCE_DRAG_TYPE, String(sequence.id));
              event.dataTransfer.effectAllowed = "move";
              setDraggingSequenceId(String(sequence.id));
            }}
            onDragEnd={() => {
              setDraggingSequenceId("");
              setSequenceDropTarget(null);
            }}
          >
            <DragIndicator fontSize="small" />
          </span>
        </Tooltip>
      </TableCell>
      <TableCell padding="checkbox">
        <Checkbox
          size="small"
          color="primary"
          checked={selectedSequenceIds.includes(String(sequence.id))}
          onChange={() => toggleSequenceSelection(sequence.id)}
          inputProps={{ "aria-label": `Seleccionar ${sequence.name}` }}
        />
      </TableCell>
      <TableCell align="center">{sequence.id}</TableCell>
      <TableCell align="center">{sequence.name}</TableCell>
      <TableCell align="center">
        {sequence.connection?.name || sequence.connection?.number || "-"}
      </TableCell>
      <TableCell align="center">{sequence.parent?.name || "-"}</TableCell>
      <TableCell align="center">
        {getMatchTypeLabel(sequence)}
      </TableCell>
      <TableCell align="center">{sequence.blocks?.length || 0} bloque(s)</TableCell>
      <TableCell align="center">{renderStatusToggle(sequence)}</TableCell>
      <TableCell align="center">{renderSequenceActions(sequence)}</TableCell>
    </TableRow>
    );
  };

  const renderMobileSequenceCard = sequence => (
    <div className={classes.mobileCard} key={sequence.id}>
      <div className={classes.mobileCardHeader}>
        <Checkbox
          size="small"
          color="primary"
          checked={selectedSequenceIds.includes(String(sequence.id))}
          onChange={() => toggleSequenceSelection(sequence.id)}
          inputProps={{ "aria-label": `Seleccionar ${sequence.name}` }}
        />
        <div>
          <Typography className={classes.mobileSequenceName}>
            {sequence.name}
          </Typography>
          <Typography className={classes.mobileSequenceMeta}>
            {sequence.category || "General"}
          </Typography>
        </div>
        <div className={classes.mobileActions}>
          {renderSequenceActions(sequence)}
        </div>
      </div>

      <Divider />

      <div className={classes.mobileCardGrid}>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>ID</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {sequence.id}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>Conexión</span>
          <Typography variant="body2" className={classes.mobileValue}>
            {sequence.connection?.name || sequence.connection?.number || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("chatbot.table.category")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {sequence.category || "General"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("chatbot.table.parent")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {sequence.parent?.name || "-"}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("chatbot.table.matchType")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {getMatchTypeLabel(sequence)}
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("chatbot.table.blocks")}
          </span>
          <Typography variant="body2" className={classes.mobileValue}>
            {sequence.blocks?.length || 0} bloque(s)
          </Typography>
        </div>
        <div className={classes.mobileField}>
          <span className={classes.mobileLabel}>
            {i18n.t("chatbot.table.status")}
          </span>
          <div className={classes.mobileValue}>{renderStatusToggle(sequence)}</div>
        </div>
      </div>
    </div>
  );

  const renderCategoryHeader = (category, count) => (
    <div className={classes.categoryHeader}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
        <Tooltip
          title={
            canReorderCategories
              ? "Arrastrar categoría"
              : "Quita la búsqueda y los filtros para ordenar categorías"
          }
        >
          <span
            className={`${classes.dragHandle} ${
              canReorderCategories ? "" : classes.dragHandleDisabled
            }`}
            draggable={canReorderCategories}
            onDragStart={event => {
              if (!canReorderCategories) return;
              event.dataTransfer.setData(CATEGORY_DRAG_TYPE, category);
              event.dataTransfer.effectAllowed = "move";
              setDraggingCategory(category);
            }}
            onDragEnd={() => {
              setDraggingCategory("");
              setCategoryDropTarget(null);
            }}
          >
            <DragIndicator fontSize="small" />
          </span>
        </Tooltip>
        <Typography variant="subtitle2" className={classes.categoryTitle} noWrap>
          {category}
        </Typography>
      </div>
      <Typography variant="body2" className={classes.categoryCount}>
        {count} secuencia{count === 1 ? "" : "s"}
      </Typography>
    </div>
  );

  const renderCategoryTable = ({ category, sequences: categorySequences }) => (
    <div
      className={`${classes.categorySection} ${
        categoryDropTarget?.category === category &&
        categoryDropTarget?.placement === "before"
          ? classes.categoryDropBefore
          : ""
      } ${
        categoryDropTarget?.category === category &&
        categoryDropTarget?.placement === "after"
          ? classes.categoryDropAfter
          : ""
      }`}
      key={category}
      onDragOver={event => {
        if (!canReorderCategories || !draggingCategory || draggingCategory === category) return;
        event.preventDefault();
        event.stopPropagation();
        const bounds = event.currentTarget.getBoundingClientRect();
        const placement =
          event.clientY > bounds.top + bounds.height / 2 ? "after" : "before";
        const nextTarget = { category, placement };
        setCategoryDropTarget(prevState =>
          prevState?.category === nextTarget.category &&
          prevState?.placement === nextTarget.placement
            ? prevState
            : nextTarget
        );
      }}
      onDrop={event => {
        if (!canReorderCategories || !draggingCategory) return;
        event.preventDefault();
        event.stopPropagation();
        moveCategory(
          draggingCategory,
          category,
          categoryDropTarget?.placement || "after"
        );
      }}
    >
      {renderCategoryHeader(category, categorySequences.length)}
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell padding="checkbox" />
            <TableCell padding="checkbox" />
            <TableCell align="center">ID</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.name")}</TableCell>
            <TableCell align="center">Conexión</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.parent")}</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.matchType")}</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.blocks")}</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.status")}</TableCell>
            <TableCell align="center">{i18n.t("chatbot.table.actions")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>{categorySequences.map(renderSequenceRow)}</TableBody>
      </Table>
    </div>
  );

  const getFlowEdgeGeometry = edge => {
    const source = flowLayout.positions[edge.sourceId];
    const target = flowLayout.positions[edge.targetId];
    if (!source || !target) return null;

    const startX = source.x + source.width;
    const startY = source.y + source.height / 2;
    const endX = target.x;
    const endY = target.y + target.height / 2;
    const distance = Math.max(80, Math.abs(endX - startX) / 2);
    const path = `M ${startX} ${startY} C ${startX + distance} ${startY}, ${endX - distance} ${endY}, ${endX} ${endY}`;
    const t = 0.5;
    const middleX =
      (1 - t) ** 3 * startX +
      3 * (1 - t) ** 2 * t * (startX + distance) +
      3 * (1 - t) * t ** 2 * (endX - distance) +
      t ** 3 * endX;
    const middleY =
      (1 - t) ** 3 * startY +
      3 * (1 - t) ** 2 * t * startY +
      3 * (1 - t) * t ** 2 * endY +
      t ** 3 * endY;

    return { path, endX, endY, middleX, middleY };
  };

  const renderFlowEdge = edge => {
    const geometry = getFlowEdgeGeometry(edge);
    if (!geometry) return null;

    const config = edgeConfig[edge.type] || edgeConfig.parent;

    return (
      <g key={edge.id}>
        <path
          d={geometry.path}
          fill="none"
          stroke={config.color}
          strokeWidth="2.5"
          strokeLinecap="round"
          opacity="0.9"
        />
        <circle cx={geometry.endX} cy={geometry.endY} r="4" fill={config.color} />
      </g>
    );
  };

  const renderFlowEdgeAction = edge => {
    if (edge.type !== "parent") return null;
    const geometry = getFlowEdgeGeometry(edge);
    if (!geometry) return null;

    return (
      <IconButton
        key={`${edge.id}-disconnect`}
        size="small"
        className={classes.flowEdgeAction}
        style={{
          left: geometry.middleX - 12,
          top: geometry.middleY - 12,
        }}
        onClick={() => setDisconnectingFlowEdge(edge)}
      >
        <Close fontSize="small" />
      </IconButton>
    );
  };

  const renderFlowNode = sequence => {
    const position = flowLayout.positions[sequence.id];
    if (!position) return null;
    const isConnectingSource = String(connectingSequenceId) === String(sequence.id);

    return (
      <div
        key={sequence.id}
        data-flow-node="true"
        className={`${classes.flowNode} ${
          sequence.isActive ? "" : classes.flowNodeInactive
        } ${isConnectingSource ? classes.flowNodeConnecting : ""} ${
          String(draggingFlowNodeId) === String(sequence.id)
            ? classes.flowNodeDragging
            : ""
        }`}
        style={{
          left: position.x,
          top: position.y,
        }}
        onMouseDown={event => startFlowNodeDrag(event, sequence)}
      >
        <span
          className={`${classes.flowHandle} ${classes.flowHandleIn} ${
            connectingSequenceId ? classes.flowHandleActive : ""
          }`}
          data-flow-handle="true"
          onDragOver={event => {
            if (!connectingSequenceId) return;
            event.preventDefault();
            event.stopPropagation();
          }}
          onDrop={event => {
            event.preventDefault();
            event.stopPropagation();
            handleFlowConnect(sequence);
          }}
          title="Soltar aquí para convertir en submenú"
        />
        <span
          className={`${classes.flowHandle} ${classes.flowHandleOut}`}
          data-flow-handle="true"
          draggable
          onDragStart={event => {
            event.dataTransfer.setData("text/sequence-source", String(sequence.id));
            event.dataTransfer.effectAllowed = "link";
            setConnectingSequenceId(String(sequence.id));
          }}
          onDragEnd={() => {
            window.setTimeout(() => setConnectingSequenceId(""), 80);
          }}
          title="Arrastrar para conectar"
        />
        <div className={classes.flowNodeHeader}>
          <div>
            <Typography className={classes.flowNodeTitle}>
              {sequence.name}
            </Typography>
            <Typography className={classes.flowNodeMeta}>
              ID {sequence.id} · {sequence.category || "General"}
            </Typography>
          </div>
          <div>
            {renderStatusToggle(sequence)}
            <IconButton
              size="small"
              onClick={event => openFlowSequenceMenu(event, sequence)}
            >
              <Close fontSize="small" />
            </IconButton>
          </div>
        </div>
        <Typography className={classes.flowNodeMeta}>
          {getMatchTypeLabel(sequence)}
          {sequence.trigger ? ` · ${sequence.trigger}` : ""}
        </Typography>
        <Typography className={classes.flowNodeMeta}>
          {sequence.blocks?.length || 0} bloque(s)
          {sequence.parent?.name ? ` · Submenú de ${sequence.parent.name}` : ""}
        </Typography>
        <div className={classes.flowNodeFooter}>
          <div>
            <Button size="small" startIcon={<Edit />} onClick={() => handleEdit(sequence)}>
              Editar
            </Button>
            <Button size="small" startIcon={<Add />} onClick={() => openNodeModal(sequence)}>
              Agregar
            </Button>
          </div>
        </div>
      </div>
    );
  };

  const renderFlowView = () => (
    <div
      className={`${classes.flowShell} ${
        isFlowFullscreen ? classes.flowShellFullscreen : ""
      }`}
    >
      <div className={classes.flowToolbar}>
        <div className={classes.flowActions}>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Flujo"
            value={selectedFlowId || NO_FLOW_AVAILABLE}
            onChange={event =>
              setSelectedFlowId(
                event.target.value === NO_FLOW_AVAILABLE ? "" : event.target.value
              )
            }
            style={{ minWidth: 220 }}
          >
            {flows.map(flow => (
              <MenuItem key={flow.id} value={flow.id}>
                {flow.name}
              </MenuItem>
            ))}
            {!flows.length && (
              <MenuItem value={NO_FLOW_AVAILABLE}>Sin flujos</MenuItem>
            )}
          </TextField>
          <IconButton
            size="small"
            disabled={!selectedFlow}
            onClick={openRenameFlowModal}
          >
            <Edit fontSize="small" />
          </IconButton>
          <IconButton
            size="small"
            disabled={!selectedFlow}
            onClick={() => setDeletingFlow(selectedFlow)}
          >
            <DeleteOutline fontSize="small" />
          </IconButton>
          <Button
            size="small"
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={openCreateFlowModal}
          >
            Flujo
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedFlow}
            startIcon={<Add />}
            onClick={() => openNodeModal(null)}
          >
            Secuencia
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={!selectedFlow}
            onClick={handleAutoArrangeFlow}
          >
            Auto organizar
          </Button>
          <TextField
            select
            size="small"
            variant="outlined"
            label="Disponible"
            value={availableSequenceId || NO_SEQUENCE_AVAILABLE}
            onChange={event =>
              setAvailableSequenceId(
                event.target.value === NO_SEQUENCE_AVAILABLE ? "" : event.target.value
              )
            }
            disabled={!selectedFlow || !availableFlowSequences.length}
            style={{ minWidth: 220 }}
          >
            {availableFlowSequences.map(sequence => (
              <MenuItem key={sequence.id} value={sequence.id}>
                {sequence.name}
              </MenuItem>
            ))}
            {!availableFlowSequences.length && (
              <MenuItem value={NO_SEQUENCE_AVAILABLE}>Sin disponibles</MenuItem>
            )}
          </TextField>
          <Button
            size="small"
            variant="outlined"
            disabled={!availableSequenceId}
            onClick={handleAddExistingSequence}
          >
            Agregar disponible
          </Button>
        </div>
        <div className={classes.flowZoomActions}>
          <Tooltip title="Reducir">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedFlow}
                onClick={() =>
                  setFlowZoom(value => Math.max(0.5, Number((value - 0.1).toFixed(2))))
                }
              >
                -
              </Button>
            </span>
          </Tooltip>
          <Typography variant="body2">{Math.round(flowZoom * 100)}%</Typography>
          <Tooltip title="Aumentar">
            <span>
              <Button
                size="small"
                variant="outlined"
                disabled={!selectedFlow}
                onClick={() =>
                  setFlowZoom(value => Math.min(1.8, Number((value + 0.1).toFixed(2))))
                }
              >
                +
              </Button>
            </span>
          </Tooltip>
          <Button
            size="small"
            variant={flowPanMode ? "contained" : "outlined"}
            color="primary"
            disabled={!selectedFlow}
            onClick={() => setFlowPanMode(value => !value)}
          >
            Mover
          </Button>
          <Button
            size="small"
            color="primary"
            variant="outlined"
            disabled={!selectedFlow}
            startIcon={isFlowFullscreen ? <FullscreenExit /> : <Fullscreen />}
            onClick={() => setIsFlowFullscreen(prevState => !prevState)}
          >
            {isFlowFullscreen ? "Minimizar" : "Ampliar"}
          </Button>
        </div>
        <div className={classes.edgeLegend}>
          {Object.entries(edgeConfig).map(([key, config]) => (
            <span key={key} className={classes.edgeLegendItem}>
              <span
                className={classes.edgeLegendColor}
                style={{ backgroundColor: config.color }}
              />
              {config.label}
            </span>
          ))}
        </div>
      </div>
      <Typography variant="body2" className={classes.flowHint} style={{ padding: "0 4px 8px" }}>
        Arrastra nodos para ordenar el flujo. Arrastra el punto derecho de una secuencia al punto izquierdo de otra para conectarlas como padre e hijo.
      </Typography>
      <div
        className={`${classes.flowCanvasWrap} ${
          isFlowFullscreen ? classes.flowCanvasWrapFullscreen : ""
        } ${
          flowPanMode ? classes.flowCanvasPannable : ""
        }`}
        ref={flowCanvasRef}
        onMouseDown={handleFlowPanMouseDown}
      >
        {!selectedFlow ? (
          <div style={{ padding: 24 }}>
            <Typography color="textSecondary">
              Crea un flujo para empezar a organizar tus secuencias.
            </Typography>
          </div>
        ) : flowLoading ? (
          <div style={{ padding: 24 }}>
            <CircularProgress size={24} />
          </div>
        ) : selectedFlowSequences.length ? (
          <div
            className={classes.flowCanvas}
            style={{
              width: flowLayout.width * flowZoom,
              height: flowLayout.height * flowZoom,
            }}
          >
            <div
              style={{
                position: "relative",
                width: flowLayout.width,
                height: flowLayout.height,
                transform: `scale(${flowZoom})`,
                transformOrigin: "0 0",
              }}
            >
              <svg
                className={classes.flowEdges}
                width={flowLayout.width}
                height={flowLayout.height}
                viewBox={`0 0 ${flowLayout.width} ${flowLayout.height}`}
              >
                {flowEdges.map(renderFlowEdge)}
              </svg>
              {flowEdges.map(renderFlowEdgeAction)}
              {selectedFlowSequences.map(renderFlowNode)}
            </div>
          </div>
        ) : (
          <div style={{ padding: 24 }}>
            <Typography color="textSecondary">
              Este flujo no tiene secuencias. Crea una secuencia o agrega una disponible.
            </Typography>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingSequence &&
          `${i18n.t("chatbot.confirmationModal.deleteTitle")} ${deletingSequence.name}?`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDelete(deletingSequence.id)}
      >
        {i18n.t("chatbot.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={
          statusToggleSequence?.nextActive
            ? "Activar secuencia"
            : "Desactivar secuencia"
        }
        open={Boolean(statusToggleSequence)}
        onClose={() => setStatusToggleSequence(null)}
        onConfirm={handleConfirmStatusToggle}
      >
        {statusToggleSequence?.nextActive
          ? `¿Activar la secuencia ${statusToggleSequence?.sequence?.name}?`
          : `¿Desactivar la secuencia ${statusToggleSequence?.sequence?.name}?`}
      </ConfirmationModal>
      <ConfirmationModal
        title={`Eliminar ${selectedSequenceCount} secuencia${
          selectedSequenceCount === 1 ? "" : "s"
        }?`}
        open={bulkDeleteOpen}
        onClose={setBulkDeleteOpen}
        onConfirm={handleDeleteSelectedSequences}
      >
        {i18n.t("chatbot.confirmationModal.deleteMessage")}
      </ConfirmationModal>
      <ConfirmationModal
        title={deletingFlow ? `Eliminar flujo ${deletingFlow.name}?` : ""}
        open={Boolean(deletingFlow)}
        onClose={() => setDeletingFlow(null)}
        onConfirm={handleDeleteFlow}
      >
        Esta accion elimina solo el flujo visual, no borra las secuencias.
      </ConfirmationModal>
      <ConfirmationModal
        title={
          flowSequenceAction?.action === "remove"
            ? "Quitar secuencia del flujo"
            : "Eliminar secuencia"
        }
        open={Boolean(flowSequenceAction)}
        onClose={() => setFlowSequenceAction(null)}
        onConfirm={handleConfirmFlowSequenceAction}
      >
        {flowSequenceAction?.action === "remove"
          ? `¿Quitar ${flowSequenceAction?.sequence?.name} de este flujo?`
          : `¿Eliminar definitivamente la secuencia ${flowSequenceAction?.sequence?.name}?`}
      </ConfirmationModal>
      <ConfirmationModal
        title="Desconectar secuencias"
        open={Boolean(disconnectingFlowEdge)}
        onClose={() => setDisconnectingFlowEdge(null)}
        onConfirm={handleFlowDisconnect}
      >
        ¿Quitar la conexión entre estas secuencias?
      </ConfirmationModal>
      <Menu
        anchorEl={flowSequenceMenuAnchor}
        open={Boolean(flowSequenceMenuAnchor)}
        onClose={closeFlowSequenceMenu}
      >
        <MenuItem onClick={() => requestFlowSequenceAction("remove")}>
          Quitar del flujo
        </MenuItem>
        <MenuItem onClick={() => requestFlowSequenceAction("delete")}>
          Eliminar secuencia
        </MenuItem>
      </Menu>
      <Dialog
        open={flowModalOpen}
        onClose={() => setFlowModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {flowModalMode === "rename" ? "Renombrar flujo" : "Nuevo flujo"}
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            variant="outlined"
            label="Nombre"
            value={flowName}
            onChange={event => setFlowName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") handleSaveFlowModal();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowModalOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!flowName.trim()}
            onClick={handleSaveFlowModal}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <Dialog
        open={nodeModalOpen}
        onClose={() => setNodeModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>{nodeParent ? "Nuevo submenú" : "Nueva secuencia"}</DialogTitle>
        <DialogContent dividers>
          {nodeParent && (
            <Typography color="textSecondary" gutterBottom>
              Padre: {nodeParent.name}
            </Typography>
          )}
          <TextField
            autoFocus
            fullWidth
            margin="dense"
            variant="outlined"
            label="Nombre"
            value={nodeName}
            onChange={event => setNodeName(event.target.value)}
            onKeyDown={event => {
              if (event.key === "Enter") handleAddNode();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNodeModalOpen(false)}>Cancelar</Button>
          <Button
            color="primary"
            variant="contained"
            disabled={!nodeName.trim()}
            onClick={handleAddNode}
          >
            Guardar
          </Button>
        </DialogActions>
      </Dialog>
      <ChatbotModal
        open={modalOpen}
        onClose={handleCloseModal}
        onSave={handleSavedModal}
        sequenceId={selectedSequence && selectedSequence.id}
        defaultConnectionId={selectedConnectionId}
        defaultConfigSections={selectedSequence ? [] : ["chatbot"]}
        categories={categories}
      />
      <MainHeader>
        <div className={classes.headerContent}>
          <Title>{i18n.t("chatbot.title")}</Title>
          <MainHeaderButtonsWrapper className={classes.headerButtons}>
            <TextField
              placeholder={i18n.t("chatbot.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              disabled={!selectedConnectionId || selectedWhatsappTab?.orphan}
              onClick={handleOpenModal}
            >
              {i18n.t("chatbot.buttons.add")}
            </Button>
          </MainHeaderButtonsWrapper>
        </div>
      </MainHeader>
      <Paper className={classes.mainPaper} variant="outlined">
        <Tabs
          value={selectedConnectionId}
          onChange={(event, value) => setSelectedConnectionId(value)}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {whatsappTabs.length ? (
            whatsappTabs.map(whatsapp => (
              <Tab
                key={whatsapp.value}
                value={whatsapp.value}
                label={whatsapp.name || whatsapp.number || `Conexión ${whatsapp.id}`}
              />
            ))
          ) : (
            <Tab value="" label="Sin conexiones" disabled />
          )}
        </Tabs>
        <div className={classes.viewSwitch}>
          <Button
            size="small"
            variant={viewMode === "list" ? "contained" : "outlined"}
            color="primary"
            onClick={() => setViewMode("list")}
          >
            Lista
          </Button>
          <Button
            size="small"
            variant={viewMode === "flow" ? "contained" : "outlined"}
            color="primary"
            startIcon={<AccountTree />}
            onClick={() => setViewMode("flow")}
          >
            Flujo
          </Button>
        </div>
        {viewMode === "list" && (
          <div className={classes.listFilters}>
            <FormControl variant="outlined" size="small">
              <InputLabel>Categoría</InputLabel>
              <Select
                value={selectedCategoryFilter}
                label="Categoría"
                onChange={event => setSelectedCategoryFilter(event.target.value)}
              >
                <MenuItem value={ALL_CATEGORIES}>Todas las categorías</MenuItem>
                {categories.map(category => (
                  <MenuItem key={category} value={category}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl variant="outlined" size="small">
              <InputLabel>Flujo</InputLabel>
              <Select
                value={selectedFlowFilter}
                label="Flujo"
                onChange={event => setSelectedFlowFilter(event.target.value)}
              >
                <MenuItem value={ALL_FLOWS}>Todos los flujos</MenuItem>
                {flows.map(flow => (
                  <MenuItem key={flow.id} value={String(flow.id)}>
                    {flow.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
        )}
        {viewMode === "list" && filteredSequences.length > 0 && (
          <div className={classes.bulkActions}>
            <Checkbox
              size="small"
              color="primary"
              checked={allSequencesSelected}
              indeterminate={selectedSequenceCount > 0 && !allSequencesSelected}
              onChange={toggleAllSequencesSelection}
              inputProps={{ "aria-label": "Seleccionar secuencias" }}
            />
            <Typography variant="body2" className={classes.bulkActionsText}>
              {selectedSequenceCount
                ? `${selectedSequenceCount} seleccionada${
                    selectedSequenceCount === 1 ? "" : "s"
                  }`
                : "Seleccionar secuencias"}
            </Typography>
            {selectedSequenceCount > 0 && (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setSelectedSequenceIds([])}
                >
                  Limpiar
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  startIcon={<DeleteOutline />}
                  onClick={() => setBulkDeleteOpen(true)}
                >
                  Eliminar
                </Button>
              </>
            )}
          </div>
        )}
        {viewMode === "flow" ? (
          renderFlowView()
        ) : (
          <>
            <div className={classes.mobileCards}>
              {groupedSequences.map(({ category, sequences: categorySequences }) => (
                <div className={classes.mobileCategorySection} key={category}>
                  {renderCategoryHeader(category, categorySequences.length)}
                  {categorySequences.map(renderMobileSequenceCard)}
                </div>
              ))}
              {!loading && !groupedSequences.length && (
                <Typography color="textSecondary">
                  No hay secuencias para los filtros seleccionados.
                </Typography>
              )}
              {loading && <CircularProgress size={24} />}
            </div>

            <div className={classes.tableWrapper}>
              {groupedSequences.map(renderCategoryTable)}
              {!loading && !groupedSequences.length && (
                <Typography color="textSecondary" style={{ padding: 16 }}>
                  No hay secuencias para los filtros seleccionados.
                </Typography>
              )}
              {loading && (
                <Table size="small">
                  <TableBody>
                    <TableRowSkeleton columns={10} />
                  </TableBody>
                </Table>
              )}
            </div>
            {selectedFlowFilter === ALL_FLOWS && (
              <div className={classes.paginationBar}>
                <TablePagination
                  component="div"
                  count={sequenceCount}
                  page={pageNumber - 1}
                  rowsPerPage={rowsPerPage}
                  rowsPerPageOptions={[20, 30, 50]}
                  labelRowsPerPage="Secuencias por pagina"
                  labelDisplayedRows={({ from, to, count }) =>
                    `${from}-${to} de ${count !== -1 ? count : `mas de ${to}`}`
                  }
                  onChangePage={handleChangePage}
                  onChangeRowsPerPage={handleChangeRowsPerPage}
                />
              </div>
            )}
          </>
        )}

      </Paper>
    </MainContainer>
  );
};

export default Chatbot;

